import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import httpClient from '../services/http-client';
import { OrderReviewDraftDto, UpsertOrderReviewDraftRequest } from '../types';

const reviewDraftsQueryKey = (divisionId: string, orderId: string) => ['order-review-drafts', divisionId, orderId];

/**
 * Loads the current reviewer's locally-staged line decisions for a RequireExplicitSubmit step,
 * so an in-progress review survives a refresh/closed tab.
 */
export function useOrderReviewDrafts(divisionId: string, orderId: string, enabled: boolean = true) {
  return useQuery<OrderReviewDraftDto[]>({
    queryKey: reviewDraftsQueryKey(divisionId, orderId),
    queryFn: async () =>
      httpClient.get<OrderReviewDraftDto[]>(
        `/api/v1.0/divisions/${divisionId}/orders/${orderId}/review-drafts`
      ),
    enabled: enabled && !!divisionId && !!orderId,
  });
}

export function useUpsertOrderReviewDraft() {
  const queryClient = useQueryClient();
  return useMutation<
    OrderReviewDraftDto,
    Error,
    { divisionId: string; orderId: string; lineItemId: string; draft: UpsertOrderReviewDraftRequest }
  >({
    mutationFn: async ({ divisionId, orderId, lineItemId, draft }) =>
      httpClient.put<OrderReviewDraftDto>(
        `/api/v1.0/divisions/${divisionId}/orders/${orderId}/review-drafts/${encodeURIComponent(lineItemId)}`,
        draft
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: reviewDraftsQueryKey(variables.divisionId, variables.orderId) });
    },
    onError: (error) => {
      console.error('Error staging order review draft:', error);
    }
  });
}

export function useDeleteOrderReviewDraft() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { divisionId: string; orderId: string; lineItemId: string }>({
    mutationFn: async ({ divisionId, orderId, lineItemId }) =>
      httpClient.delete<void>(
        `/api/v1.0/divisions/${divisionId}/orders/${orderId}/review-drafts/${encodeURIComponent(lineItemId)}`
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: reviewDraftsQueryKey(variables.divisionId, variables.orderId) });
    },
    onError: (error) => {
      console.error('Error undoing order review draft:', error);
    }
  });
}

export function useDeleteAllOrderReviewDrafts() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { divisionId: string; orderId: string }>({
    mutationFn: async ({ divisionId, orderId }) =>
      httpClient.delete<void>(`/api/v1.0/divisions/${divisionId}/orders/${orderId}/review-drafts`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: reviewDraftsQueryKey(variables.divisionId, variables.orderId) });
    },
    onError: (error) => {
      console.error('Error discarding order review drafts:', error);
    }
  });
}
